import React, { useCallback, useEffect, useState } from "react";
import MessengerLayout from "../../layout/MessengerLayout";
import {
  UserCog, Calendar, Banknote, Target, ClipboardCheck,
  Folders, Heart, MessageSquare, Video, GraduationCap,
  Receipt, Shield, Gavel, Check, Lock, AlertTriangle,
} from "lucide-react";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";

const cardCls =
  "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
  "Content-Type": "application/json",
});

// Visual catalogue. Keys MUST match the backend ALL_MODULES list.
//
// THE SECOND OF FOUR HARDCODED COPIES of the same catalogue — the others
// are modules/routes.py ALL_MODULES (authoritative), the superadmin
// Modules screen (which additionally carries the prices) and the demo
// session picker. Nothing reconciles them, and adding a module means
// editing all four; see the block above MODULES in
// superadmin/SuperadminModules.jsx.
//
// WHAT THIS SCREEN IS FOR, AND WHAT IT IS NOT. This is the TENANT'S own
// admin switching their own organisation's modules on and off. It is not
// a billing surface and carries no prices — what a module costs is
// Kwery's commercial matter and belongs on the superadmin screen.
const MODULES = [
  { key: "core_hr",         name: "Core HR",            icon: UserCog,         protected: true,
    description: "User profiles, org, departments — required by every module." },
  { key: "leave",           name: "Leave",              icon: Calendar,        description: "Leave requests, balances and approvals." },
  { key: "payroll",         name: "Payroll",            icon: Banknote,
    description: "Pay runs, payslips, statutory deductions. Includes Expenses + Benefits as sub-tabs." },
  { key: "performance",     name: "Performance",        icon: Target,          description: "OKRs, reviews and 1:1s." },
  { key: "attendance",      name: "Attendance",         icon: ClipboardCheck,  description: "Daily attendance, shifts and timesheets." },
  { key: "projects",        name: "Projects & Tasks",   icon: Folders,         description: "Project tracking and task assignment." },
  { key: "engagement",      name: "Engagement",         icon: Heart,           description: "Pulse surveys and recognition." },
  { key: "ai_chat",         name: "KweryAI Chat",       icon: MessageSquare,   protected: true,
    description: "K.I.R.A AI assistant. Always on." },
  { key: "video_messaging", name: "Video & Messaging",  icon: Video,           description: "In-app messaging and video calls." },
  { key: "learning",        name: "Learning",           icon: GraduationCap,   description: "Courses, certifications and compliance training." },
  { key: "expenses",        name: "Expenses",           icon: Receipt,         description: "Expense claims and approvals. Lives under Payroll." },
  { key: "benefits",        name: "Benefits",           icon: Shield,          description: "Health, pension and other benefit schemes." },
  // ER case management (task 16). NOT protected: a tenant that has not
  // bought it should have it off, and one that has may switch it off
  // again. core_hr and ai_chat carry `protected: true` because the
  // backend's PROTECTED_MODULES refuses to disable them and a toggle
  // that fails is worse than one that is visibly locked.
  //
  // DEFAULT-OFF at the backend, so an org that has never been given it
  // shows this card OFF rather than ON — which is the point of
  // DEFAULT_OFF_MODULES in modules/routes.py.
  //
  // The description says what the module IS and nothing about any case:
  // this screen is read by a tenant admin who may hold no party row on
  // anything, and case content is granted per person per case.
  { key: "er_cases",        name: "ER Cases",           icon: Gavel,
    description: "Disciplinary and grievance case management. Access to any individual case is granted per person, per case — enabling the module does not open case records to administrators." },
];

function Toggle({ on, onChange, disabled, locked }) {
  return (
    <button
      type="button"
      onClick={() => !disabled && !locked && onChange(!on)}
      disabled={disabled || locked}
      title={locked ? "Core module — always on" : ""}
      className={
        "relative inline-flex items-center h-6 w-11 rounded-full transition " +
        ((disabled || locked) ? "opacity-50 cursor-not-allowed " : "") +
        (on ? "bg-[#00FF99]" : "bg-white/15")
      }
      aria-pressed={on}
    >
      <span
        className={
          "inline-block h-4 w-4 rounded-full bg-black transform transition " +
          (on ? "translate-x-6" : "translate-x-1")
        }
      />
    </button>
  );
}

export default function AdminModulesPage() {
  let user = null;
  try { user = JSON.parse(localStorage.getItem("user") || "null"); } catch {}
  const isAdmin = !!(user && (user.role === "admin" || user.role === "executive" || user.role === "superadmin"));

  const [enabled, setEnabled] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [savedKey, setSavedKey] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/api/modules/admin/my-modules`, { headers: authHeaders() });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setError(err.detail || `Failed to load modules (${res.status})`);
        return;
      }
      const data = await res.json();
      const map = {};
      for (const m of data.modules || []) map[m.module_key] = !!m.is_enabled;
      setEnabled(map);
    } catch (e) {
      setError("Network error loading modules.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (isAdmin) load(); else setLoading(false); }, [isAdmin, load]);

  const handleToggle = async (key, next) => {
    const prev = !!enabled[key];
    setEnabled((s) => ({ ...s, [key]: next }));
    setSavingKey(key); setSavedKey(null); setError("");
    try {
      const res = await fetch(`${API_BASE}/api/modules/admin/${key}`, {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify({ enabled: next }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Save failed (${res.status})`);
      }
      setSavedKey(key);
      setTimeout(() => setSavedKey((k) => (k === key ? null : k)), 1500);
    } catch (e) {
      setEnabled((s) => ({ ...s, [key]: prev }));
      setError(e.message || "Save failed.");
    } finally {
      setSavingKey((k) => (k === key ? null : k));
    }
  };

  if (!isAdmin) {
    return (
      <MessengerLayout>
        <div className="p-8">
          <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-6 text-red-300 text-sm">
            Access denied — admin role required.
          </div>
        </div>
      </MessengerLayout>
    );
  }

  const enabledCount = MODULES.filter((m) => enabled[m.key]).length;

  return (
    <MessengerLayout>
      <div className="p-6 text-white space-y-5">
        <div>
          <h1 className="text-2xl font-bold text-[#00ff90]">Modules</h1>
          <p className="text-white/40 text-sm mt-0.5">
            Enable or disable workspace modules for your organisation.
            Core modules cannot be disabled.
          </p>
        </div>

        <div className={`${cardCls} p-4 flex items-center justify-between gap-4`}>
          <div className="text-sm text-white/70">
            <strong className="text-[#00ff90]">{loading ? "…" : enabledCount}</strong> of {MODULES.length} modules enabled
          </div>
          <button onClick={load} className="text-xs text-white/60 hover:text-white px-3 py-1.5 rounded border border-white/10 hover:bg-white/5">
            Refresh
          </button>
        </div>

        {error && (
          <div className="rounded-lg border border-red-500/40 bg-red-500/10 text-red-300 px-4 py-2 text-sm flex items-center gap-2">
            <AlertTriangle size={14} /> {error}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {MODULES.map((m) => {
            const Icon = m.icon;
            const on = !!enabled[m.key];
            const isSaving = savingKey === m.key;
            const isSaved  = savedKey === m.key;
            const locked   = !!m.protected;
            return (
              <div key={m.key} className={`${cardCls} p-5`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex gap-3 min-w-0 flex-1">
                    <div className={
                      "w-10 h-10 rounded-lg flex items-center justify-center shrink-0 " +
                      (on ? "bg-[#00FF99]/10 text-[#00FF99]" : "bg-white/5 text-white/40")
                    }>
                      <Icon size={20} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-white truncate flex items-center gap-2">
                        {m.name}
                        {locked && (
                          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-white/5 border border-white/10 rounded px-1.5 py-0.5 text-white/60">
                            <Lock size={9} /> Core
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-white/50 mt-1 leading-snug">{m.description}</div>
                      {isSaving && <div className="text-xs text-white/40 mt-2">Saving…</div>}
                      {isSaved && (
                        <div className="text-xs text-[#00FF99] mt-2 flex items-center gap-1">
                          <Check size={12} /> Saved
                        </div>
                      )}
                    </div>
                  </div>
                  <Toggle
                    on={on}
                    onChange={(next) => handleToggle(m.key, next)}
                    disabled={isSaving || loading}
                    locked={locked}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </MessengerLayout>
  );
}
